import React from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";

interface EmailPieChartProps {
  data: any[];
  title: string;
  description?: string;
  dataKey: string;
  nameKey: string;
  colors?: string[];
  className?: string;
}

export function EmailPieChart({ 
  data, 
  title, 
  description, 
  dataKey, 
  nameKey,
  colors = ["hsl(var(--primary))", "hsl(var(--success))", "hsl(var(--warning))", "hsl(var(--info))"],
  className 
}: EmailPieChartProps) {
  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        <div className="h-[300px] flex items-center justify-center">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={100}
                fill="hsl(var(--primary))"
                dataKey={dataKey}
                nameKey={nameKey}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              >
                {data.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={colors[index % colors.length]} 
                    className="stroke-background hover:opacity-80 transition-opacity"
                    strokeWidth={2}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ 
                  backgroundColor: "hsl(var(--background))",
                  borderColor: "hsl(var(--border))",
                  borderRadius: "var(--radius)",
                  boxShadow: "var(--shadow)"
                }}
                formatter={(value, name) => [`${value} emails`, name]}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
      <CardFooter>
        <div className="w-full flex flex-wrap gap-2 justify-center">
          {data.map((entry, index) => (
            <div
              key={`legend-${index}`}
              className="flex items-center gap-1"
            >
              <div
                className="w-3 h-3 rounded-full"
                style={{
                  backgroundColor: colors[index % colors.length],
                }}
              />
              <span className="text-sm">
                {entry[nameKey]}: {entry[dataKey]} emails
              </span>
            </div>
          ))}
        </div>
      </CardFooter>
    </Card>
  );
}
